import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMultiSelect, useSelect, useSwitch } from '@chilecompra/react-kit'
import { Card, DataTable, MultiSelect, Select, Switch, Typography } from '@chilecompra/react-kit/components'
import { exportToCsv } from '../../modules/utils/exports'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import { OPTION_FILTER_SECTORS, OPTION_ALL } from '../../config/settings/constants'
import { generateYears } from '../../modules/utils/years'
import { onGetAgilePurchaseBuyersThunk } from './AgilePurchaseBuyers.actions'
import { Content, GridContainer, GridItem, Description, Link, Root, Title } from './AgilePurchaseBuyers.styles'
import { formatAmount } from '../../modules/utils/formatters'
import { SORT_DIRECTIONS_DICTIONARY } from '@chilecompra/react-kit/components/Tables/DataTable.utils'

/**
 * The AgilePurchaseBuyer's container.
 */
const AgilePurchaseBuyers = () => {
  const dispatch = useDispatch()
  const { endYear, latestUpdate, loading, sectors: sectorOptions = [] } = useSelector(state => state.topNavbar)

  const { csv, data, loading: loadingData } = useSelector(state => state.agilePurchaseBuyers)

  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue =>
      dispatch(onGetAgilePurchaseBuyersThunk({ year: updatedValue, sectorsIds: sectorsValues, sectorOptions })),
    overwriteCallback: () => endYear
  })

  const {
    values: sectorsValues,
    onChange: handleChangeSectors,
    onDelete: handleDeleteSectors
  } = useMultiSelect({
    initialValues: OPTION_FILTER_SECTORS,
    changeCallback: updatedValues =>
      dispatch(onGetAgilePurchaseBuyersThunk({ year: yearValue, sectorsIds: updatedValues, sectorOptions })),
    formatCallbacks: [
      (updatedValues = []) => {
        const [firstValue] = updatedValues
        const indexOptionAll = OPTION_FILTER_SECTORS.indexOf(OPTION_ALL)
        if (
          updatedValues[0] === OPTION_FILTER_SECTORS[indexOptionAll + 1] &&
          OPTION_FILTER_SECTORS.length === updatedValues.length + 1
        ) {
          return [0]
        }
        if (OPTION_FILTER_SECTORS.length === updatedValues.length + 1 && !updatedValues.includes(OPTION_ALL)) {
          return OPTION_FILTER_SECTORS
        }
        if (updatedValues.includes(0) && updatedValues.length > 1) {
          const index = updatedValues.indexOf(0)
          updatedValues.splice(index, 1)
        }
        if (firstValue === OPTION_ALL && updatedValues.length === OPTION_FILTER_SECTORS.length - 1) {
          const index = updatedValues.indexOf(OPTION_ALL)
          updatedValues.splice(index, 1)
        }
        if (updatedValues.length !== OPTION_FILTER_SECTORS.length && updatedValues.length > 1) {
          updatedValues.sort((a, b) => a - b)
        }
        if (updatedValues.includes(OPTION_ALL)) {
          return OPTION_FILTER_SECTORS
        }
        if (updatedValues.length === 0) {
          return [0]
        }
        return updatedValues
      }
    ]
  })

  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()

  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    downloadFileCallback: () => exportToCsv(csv, `CompraAgil_Instituciones_${yearValue}`)
  })

  const yearsOptions = useMemo(() => generateYears(endYear), [endYear])

  useEffect(() => {
    if (!endYear) return
    dispatch(onGetAgilePurchaseBuyersThunk({ year: endYear, sectorsIds: sectorsValues, sectorOptions }))
  }, [endYear])

  return (
    <Root>
      <Content>
        <Typography
          color="primary"
          fontSize="18px"
          fontWeight="bold"
          lineHeight="21px"
          margin="0 0 24px"
          variant="body1"
        >
          Compradores en Compra Ágil
        </Typography>
        <GridContainer>
          <GridItem xs={5}>
            <Title>Compradores que transan a través de Compra Ágil</Title>
          </GridItem>
        </GridContainer>
        <GridContainer padding="0 0 28px">
          <GridItem xs={4}>
            <Description>
              Revisa los montos y la cantidad de órdenes de compra realizadas mediante Compra Ágil por los organismos
              compradores.
            </Description>
          </GridItem>
        </GridContainer>

        <DataLayout
          disableCaptureImage={disableCaptureImage}
          disableChart
          disableDownloadFile={disableDownloadFile}
          header={
            <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
              <GridItem md={10} xs={12}>
                <GridContainer alignItems="center" spacing={1}>
                  <GridItem md={3} xs={12}>
                    <Select
                      label="Año"
                      loading={loading}
                      options={yearsOptions}
                      value={yearValue}
                      onChange={handleChangeYear}
                    />
                  </GridItem>
                  <GridItem md={4} xs={12}>
                    <MultiSelect
                      label="Sector del Estado"
                      loading={loading}
                      options={sectorOptions}
                      values={sectorsValues}
                      onChange={handleChangeSectors}
                      onDelete={handleDeleteSectors}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem md={2} xs={12}>
                <Switch
                  checked={currencyValue}
                  color="primary"
                  labelLeft="CLP"
                  labelRight="USD"
                  onChange={handleChangeCurrency}
                  margin="0 0 0 32px"
                />
              </GridItem>
            </GridContainer>
          }
          initialDisplayedView={DataLayoutViews.TABLE}
          noData={!loadingData && data?.length === 0}
          noDataText="El organismo comprador no cuenta con información relacionada a sus proveedores durante el año seleccionado."
          ref={dataLayoutRef}
          updatedDate={latestUpdate}
          onCaptureImage={handleCaptureImage}
          onChangeView={handleChangeView}
          onDownloadFile={handleDownloadFile}
        >
          <DataLayoutChild view={DataLayoutViews.TABLE}>
            <Card color="blank" elevation="1" margin="0 0 32px">
              <GridContainer padding="0 0 20px">
                <GridItem xs={12}>
                  <DataTable
                    headers={[
                      { label: 'Nombre comprador', value: 'buyer' },
                      { label: 'N° de cotizaciones recibidas', value: 'totalQuotations' },
                      { label: 'Monto transado', value: 'amount' },
                      { label: 'N° de órdenes de compra', value: 'transactions' }
                    ]}
                    headersCellsProps={[
                      { enableSort: true, width: '45%' },
                      { enableSort: true, width: '20%' },
                      {
                        enableSort: true,
                        initialDirection: SORT_DIRECTIONS_DICTIONARY.DESC,
                        initialSort: true,
                        width: '15%'
                      },
                      { enableSort: true, width: '20%' }
                    ]}
                    loading={loading}
                    records={data}
                    initialRows={15}
                    recordsDecorators={[
                      ({ buyer, entCode }) => <Link to={`/ficha-organismo/${entCode}`}>{buyer}</Link>,
                      ({ totalQuotations }) => totalQuotations,
                      ({ amount, amountUSD }) =>
                        currencyValue ? `USD ${formatAmount(amountUSD)}` : `$${formatAmount(amount)}`,
                      ({ transactions }) => transactions
                    ]}
                  />
                </GridItem>
              </GridContainer>{' '}
            </Card>
          </DataLayoutChild>
        </DataLayout>
      </Content>
    </Root>
  )
}

export default AgilePurchaseBuyers
